// Opportunity engine for the Negocio section of /dashboard/analytics.
//
// A chart shows state; an insight compares state against an expectation, adds
// time, and names an action. Every rule here reads data the view already has
// in memory — no new queries, no schema. Adding a signal = adding one entry to
// RULES. Pure and React-free so it can be unit-tested.

import { buildRoute } from "@u/routes";

// ── Thresholds ───────────────────────────────────────────────────────────────
// Deliberately named and grouped: these are the product's opinion about what
// "too slow" means, and they are the first thing to tune with real usage.
export const THRESHOLDS = {
  /** A `new` lead older than this is losing heat. */
  leadStaleDays: 3,
  /** A hot/warm contact untouched for this long is cooling off. */
  contactCoolingDays: 14,
  /** A pending booking older than this tends to die unanswered. */
  bookingPendingHours: 48,
  /** Sessions a tour needs before "it gets traffic but no leads" is meaningful. */
  trafficNoLeadsMinSessions: 30,
  /** Conversion drop (percentage points) vs the previous window worth flagging. */
  conversionDropPoints: 10,
} as const;

const DAY_MS = 86400000;
const HOUR_MS = 3600000;

export type InsightSeverity = "urgent" | "warning" | "opportunity";

export interface Insight {
  id: string;
  severity: InsightSeverity;
  /** i18n key for the headline; receives `{{count}}` (and `{{name}}` where set). */
  titleKey: string;
  /** i18n key for the one-line explanation of why it matters. */
  bodyKey: string;
  /** i18n key for the action button. */
  ctaKey: string;
  /** Interpolation values for the title. */
  count: number;
  name?: string;
  /** Destination of the action — always a real, filtered view. */
  to: string;
}

// Structural inputs: only the fields each rule reads, so callers can pass their
// own richer row types without adapting them (same trick as LeadLike).
export interface InsightLead {
  status: string;
  created_at: string;
}
export interface InsightContact {
  status: string;
  last_activity_at: string;
}
export interface InsightBooking {
  status: string;
  created_at: string;
}
export interface InsightProperty {
  status: string | null;
  title: string;
  leadCount: number;
  bookingCount: number;
}
/** One row per tour: traffic in the window vs leads captured in the window. */
export interface InsightTourTraffic {
  id: string;
  title: string;
  sessions: number;
  leads: number;
}

export interface InsightInput {
  now: number;
  /** Length of the analysed window, in days (the page's period picker). */
  days: number;
  /** Leads inside the window. */
  periodLeads: InsightLead[];
  /** Leads of the window immediately before it — powers the trend comparison. */
  previousLeads: InsightLead[];
  contacts: InsightContact[];
  bookings: InsightBooking[];
  properties: InsightProperty[];
  tourTraffic: InsightTourTraffic[];
}

const ms = (iso: string) => new Date(iso).getTime();

/** Won / total, in whole percentage points. 0 when there are no leads. */
export function leadConversion(leads: InsightLead[]): number {
  if (leads.length === 0) return 0;
  const won = leads.filter((l) => l.status === "won").length;
  return Math.round((won / leads.length) * 100);
}

type Rule = (i: InsightInput) => Insight | null;

const RULES: Rule[] = [
  // 1. New leads going cold — the most expensive thing to ignore.
  (i) => {
    const cutoff = i.now - THRESHOLDS.leadStaleDays * DAY_MS;
    const count = i.periodLeads.filter(
      (l) => l.status === "new" && ms(l.created_at) < cutoff,
    ).length;
    if (!count) return null;
    return {
      id: "leads_stale",
      severity: "urgent",
      titleKey: "insight_leads_stale_title",
      bodyKey: "insight_leads_stale_body",
      ctaKey: "insight_cta_leads",
      count,
      // Lands on the contacts list already filtered to the new ones (the CRM
      // list reads `f.status` from the URL via useDashboardQueryState).
      to: `${buildRoute.contacts()}?f.status=new`,
    };
  },

  // 3. Pending bookings die unanswered — the visitor already raised their hand.
  (i) => {
    const cutoff = i.now - THRESHOLDS.bookingPendingHours * HOUR_MS;
    const count = i.bookings.filter(
      (b) => b.status === "pending" && ms(b.created_at) < cutoff,
    ).length;
    if (!count) return null;
    return {
      id: "bookings_pending",
      severity: "urgent",
      titleKey: "insight_bookings_pending_title",
      bodyKey: "insight_bookings_pending_body",
      ctaKey: "insight_cta_bookings",
      count,
      to: buildRoute.bookings("pending"),
    };
  },

  // 4. Hot/warm contacts nobody has touched in a while.
  (i) => {
    const cutoff = i.now - THRESHOLDS.contactCoolingDays * DAY_MS;
    const count = i.contacts.filter(
      (c) =>
        (c.status === "hot" || c.status === "warm") &&
        ms(c.last_activity_at) < cutoff,
    ).length;
    if (!count) return null;
    return {
      id: "contacts_cooling",
      severity: "warning",
      titleKey: "insight_contacts_cooling_title",
      bodyKey: "insight_contacts_cooling_body",
      ctaKey: "insight_cta_contacts",
      count,
      to: buildRoute.contacts(),
    };
  },

  // 6. Conversion falling vs the previous window of the same length. Needs a
  //    non-empty previous window, otherwise "it dropped" is meaningless.
  (i) => {
    if (i.previousLeads.length === 0 || i.periodLeads.length === 0) return null;
    const drop = leadConversion(i.previousLeads) - leadConversion(i.periodLeads);
    if (drop < THRESHOLDS.conversionDropPoints) return null;
    return {
      id: "conversion_drop",
      severity: "warning",
      titleKey: "insight_conversion_drop_title",
      bodyKey: "insight_conversion_drop_body",
      ctaKey: "insight_cta_leads",
      count: drop,
      to: buildRoute.leads(),
    };
  },

  // 7. THE cross-signal: a tour people watch but that captures nobody. Only
  //    analytics can see this — neither /leads nor the audience view alone can.
  (i) => {
    const worst = i.tourTraffic
      .filter(
        (tr) =>
          tr.sessions >= THRESHOLDS.trafficNoLeadsMinSessions && tr.leads === 0,
      )
      .sort((a, b) => b.sessions - a.sessions)[0];
    if (!worst) return null;
    return {
      id: "tour_traffic_no_leads",
      severity: "opportunity",
      titleKey: "insight_traffic_no_leads_title",
      bodyKey: "insight_traffic_no_leads_body",
      ctaKey: "insight_cta_tour",
      count: worst.sessions,
      name: worst.title,
      to: buildRoute.detail(worst.id),
    };
  },

  // 8. Listed property that has never drawn a single lead or booking.
  (i) => {
    const count = i.properties.filter(
      (p) => p.status === "available" && !p.leadCount && !p.bookingCount,
    ).length;
    if (!count) return null;
    return {
      id: "properties_no_interest",
      severity: "opportunity",
      titleKey: "insight_properties_cold_title",
      bodyKey: "insight_properties_cold_body",
      ctaKey: "insight_cta_properties",
      count,
      to: buildRoute.tours(),
    };
  },
];

const SEVERITY_RANK: Record<InsightSeverity, number> = {
  urgent: 0,
  warning: 1,
  opportunity: 2,
};

/**
 * Run every rule and return the surviving insights, most urgent first and —
 * within a severity — biggest first. `limit` keeps the list scannable; the
 * caller says how many it has room for.
 */
export function buildInsights(input: InsightInput, limit = 5): Insight[] {
  return RULES.map((rule) => rule(input))
    .filter((x): x is Insight => x !== null)
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
        b.count - a.count,
    )
    .slice(0, limit);
}
